import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import authService from "../../services/authService";
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthHeader,
  AuthLink,
  AuthStatus,
  PasswordInput,
} from "./AuthPrimitives";
import { isPasswordValid } from "../../utils/passwordPolicy";
import { PasswordRequirements } from "./PasswordRequirements";

export const ConfirmEmailForm: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showExpiredLink, setShowExpiredLink] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Verificar se há token na URL
  useEffect(() => {
    if (!token) {
      setError(t("Token de confirmação não encontrado na URL"));
    }
  }, [token, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) {
      setError("");
      setShowExpiredLink(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.password) {
      setError(t("Por favor, digite sua senha"));
      return false;
    }

    if (!isPasswordValid(formData.password)) {
      setError(
        t("A senha não atende a todos os requisitos de segurança exigidos."),
      );
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("As senhas não coincidem"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t("Token de confirmação inválido"));
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    setError("");
    setShowExpiredLink(false);

    try {
      const response = await authService.confirmEmail(token, formData.password);

      if (response.success) {
        setShowSuccess(true);
        console.log("✅ [ConfirmEmail] Email confirmado com sucesso");

        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate("/auth");
        }, 3000);
      } else {
        setError(response.message || t("Erro ao confirmar email"));
      }
    } catch (error) {
      let errorMessage = t("Erro ao confirmar email. Tente novamente.");
      let expired = false;

      if (error instanceof Error) {
        const message = error.message;

        if (message.includes("Token de verificacao invalido")) {
          errorMessage = t("Token de confirmação inválido ou expirado.");
        } else if (message.includes("Token de verificacao expirado")) {
          errorMessage = t(
            "Token de confirmação expirado. Solicite um novo cadastro.",
          );
          expired = true;
        } else if (message.includes("caracteres")) {
          errorMessage = t(
            "A senha não atende a todos os requisitos de segurança exigidos.",
          );
        } else {
          errorMessage = message;
        }
      }

      setError(errorMessage);
      setShowExpiredLink(expired);
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de sucesso
  if (showSuccess) {
    return (
      <div className="space-y-6">
        <AuthStatus title={t("Conta confirmada!")}>
          <p>
            {t(
              "Seu email foi confirmado e sua senha foi definida com sucesso!",
            )}
          </p>
        </AuthStatus>
        <AuthAlert
          variant="success"
          title={t("Tudo pronto!")}
          message={t(
            "Você será redirecionado para a página de login em instantes...",
          )}
        />
        <AuthButton type="button" onClick={() => navigate("/auth")}>
          {t("Ir para Login")}
        </AuthButton>
      </div>
    );
  }

  // Formulário de confirmação
  return (
    <div className="space-y-5">
      <AuthHeader
        title={t("Confirmar Email")}
        subtitle={t("Defina sua senha para ativar sua conta")}
      />

      {error && (
        <AuthAlert variant="error" message={error}>
          {showExpiredLink && (
            <AuthLink
              onClick={() => navigate("/auth")}
              className="text-sm underline"
            >
              {t("Voltar para cadastro")}
            </AuthLink>
          )}
        </AuthAlert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <AuthField label={t("Nova Senha")} htmlFor="password" required>
          <PasswordInput
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder={t("Digite sua nova senha")}
            required
            autoFocus
          />
        </AuthField>

        <AuthField
          label={t("Confirmar Senha")}
          htmlFor="confirmPassword"
          required
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder={t("Confirme sua nova senha")}
            required
          />
        </AuthField>

        <PasswordRequirements
          password={formData.password}
          confirmPassword={formData.confirmPassword}
        />

        <AuthButton
          type="submit"
          loading={isLoading}
          loadingText={t("Confirmando...")}
          disabled={!token}
        >
          {t("Confirmar Email e Definir Senha")}
        </AuthButton>
      </form>

      <p className="text-center text-sm">
        <AuthLink onClick={() => navigate("/auth")}>
          {t("Voltar para página de login")}
        </AuthLink>
      </p>
    </div>
  );
};
